import axios from 'axios';

// Configure axios to include credentials
axios.defaults.withCredentials = true;

// Use the proxy instead of the full URL
const API_URL = '/api';

/**
 * Get opportunity data by ID
 * @param {string} opportunityId - Salesforce opportunity ID
 * @returns {Promise<Object>} Opportunity data
 */
export const getOpportunityById = async (opportunityId) => {
  try {
    const response = await axios.get(`${API_URL}/salesforce/opportunity/${opportunityId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching Salesforce opportunity with ID ${opportunityId}:`, error);
    throw error;
  }
};

/**
 * Get mapped opportunity data
 * @param {string} opportunityId - Salesforce opportunity ID
 * @returns {Promise<Object>} Mapped opportunity data
 */
export const getMappedOpportunityData = async (opportunityId) => {
  try {
    console.log(`Requesting mapped opportunity data for ID: ${opportunityId}`);
    const response = await axios.get(`${API_URL}/salesforce/opportunity/${opportunityId}/mapped`);
    
    console.log(`[CLIENT] === RECEIVED OPPORTUNITY DATA FROM SERVER ===`);
    console.log(JSON.stringify(response.data, null, 2));
    
    // Check for specific fields we're having issues with
    console.log(`[CLIENT] === CHECKING SPECIFIC FIELDS ===`);
    console.log(`Federal Tax ID field: "${response.data?.opportunityData?.FederalTaxID}"`);
    console.log(`SIC Code field: "${response.data?.opportunityData?.SICCode}"`);
    
    // If the response data contains raw Salesforce fields, transform them to match our form fields
    if (response.data) {
      // Define our expected form structure
      const emptyFormData = {
        // Account Information
        BusinessLegalName: '',
        DBA: '',
        Industry: '',
        EntityType: '',
        FederalTaxID: '',
        AnnualRevenue: '',
        RequestedAmount: '',
        AverageBankBalance: '',
        BusinessStartDate: '',
        SICCode: '',
        
        // Business Address
        BusinessStreet: '',
        BusinessCity: '',
        BusinessState: '',
        BusinessZipCode: '',
        BusinessCountry: '',
        
        // Primary Contact
        ContactFirstName: '',
        ContactLastName: '',
        ContactTitle: '',
        ContactSSN: '',
        ContactDateOfBirth: '',
        ContactEmail: '',
        ContactCellPhone: '',
        ContactOwnershipPercentage: '',
        
        // Contact Address
        ContactStreet: '',
        ContactCity: '',
        ContactState: '',
        ContactZipCode: '',
        ContactCountry: ''
      };
      
      // Ensure we have our expected structure
      const formattedData = {
        ...response.data,
        opportunityData: {
          ...emptyFormData,
          ...(response.data.opportunityData || {})
        }
      };
      
      // Client-side check to ensure SIC Code is never empty
      if (!formattedData.opportunityData.SICCode || formattedData.opportunityData.SICCode === '') {
        console.log('[CLIENT] SIC Code is empty, setting default value "0000"');
        formattedData.opportunityData.SICCode = "0000";
      }
      
      console.log(`[CLIENT] === FORMATTED DATA (AFTER MERGING WITH EMPTY TEMPLATE) ===`);
      Object.entries(formattedData.opportunityData).forEach(([field, value]) => {
        const originalValue = response.data.opportunityData?.[field];
        if (originalValue !== value) {
          console.log(`${field}: Changed from "${originalValue}" to "${value}"`);
        } else {
          console.log(`${field}: "${value}" (unchanged)`);
        }
      });
      
      return formattedData;
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error getting mapped opportunity data for ${opportunityId}:`, error);
    throw error;
  }
};

/**
 * Update opportunity with form data
 * @param {string} opportunityId - Salesforce opportunity ID
 * @param {object} formData - Form data to update
 * @returns {Promise<Object>} Result of update operation
 */
export const updateOpportunityWithFormData = async (opportunityId, formData) => {
  try {
    console.log(`Updating Salesforce opportunity ${opportunityId} with form data:`, formData);
    
    const response = await axios.post(`${API_URL}/salesforce/opportunity/${opportunityId}/update`, {
      formData
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating Salesforce opportunity:', error);
    throw error;
  }
};

/**
 * Alias for getMappedOpportunityData with more appropriate name
 * @param {string} opportunityId - Salesforce opportunity ID
 * @returns {Promise<Object>} Mapped business data
 */
export const getMappedBusinessData = getMappedOpportunityData;

/**
 * Get files associated with an opportunity from Salesforce
 * @param {string} opportunityId - Salesforce opportunity ID
 * @returns {Promise<Array>} Array of file metadata
 */
export const getOpportunityFiles = async (opportunityId) => {
  try {
    console.log(`Fetching files for opportunity ${opportunityId}`);
    const response = await axios.get(`${API_URL}/salesforce/opportunity/${opportunityId}/files`);
    
    return response.data.files || [];
  } catch (error) {
    console.error(`Error fetching Salesforce files for opportunity ${opportunityId}:`, error);
    throw error;
  }
};

/**
 * Get the download URL for a specific file
 * @param {string} fileId - Salesforce file ID
 * @param {string} opportunityId - Salesforce opportunity ID
 * @param {string} [source='ContentDocument'] - Salesforce file source type
 * @returns {string} URL to download the file
 */
export const getFileDownloadUrl = (fileId, opportunityId, source = 'ContentDocument') => {
  return `${API_URL}/salesforce/files/${fileId}/download?opportunityId=${encodeURIComponent(opportunityId)}&source=${encodeURIComponent(source)}`;
};

/**
 * Get industry picklist values from Salesforce
 * @returns {Promise<Array>} Array of industry values
 */
export const getIndustryPicklistValues = async () => {
  console.log('=== getIndustryPicklistValues function called ===');
  console.log('API_URL:', API_URL);
  
  try {
    console.log('Fetching industry picklist values from API...');
    console.log('Making request to:', `${API_URL}/salesforce/industries`);
    
    const response = await axios.get(`${API_URL}/salesforce/industries`);

    console.log('API response status:', response.status);
    console.log('API response data:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch industry picklist values');
    }

    console.log('Successfully fetched industries:', response.data.industries?.length || 0, 'items');
    console.log('First few industries:', response.data.industries?.slice(0, 3));
    return response.data.industries;
  } catch (error) {
    console.error('Error fetching industry picklist values:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    throw error;
  }
};

export default {
  getOpportunityById,
  getMappedOpportunityData,
  getMappedBusinessData,
  updateOpportunityWithFormData,
  getOpportunityFiles,
  getFileDownloadUrl,
  getIndustryPicklistValues
}; 